// ABOUTME: Provider component that renders UpgradeModal based on UpgradeContext state.
// ABOUTME: Handles upgrade flow by creating Stripe checkout sessions and redirecting.

import { useCallback, useState, type ReactNode } from 'react';
import { useUpgrade } from '@/context/UpgradeContext';
import { useApp } from '@/context/AppContext';
import { UpgradeModal } from './UpgradeModal';
import { billingApi } from '@/services/api/billingClient';

interface UpgradeModalProviderProps {
  children: ReactNode;
}

export function UpgradeModalProvider({ children }: UpgradeModalProviderProps) {
  const { showUpgradeModal, limitInfo, hideModal } = useUpgrade();
  const { isSelfService, isLoadingWorkspaces } = useApp();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleUpgrade = useCallback(async () => {
    setCheckoutError(null);
    try {
      // Build success and cancel URLs based on current location
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?upgrade=success`;
      const cancelUrl = `${currentUrl}?upgrade=cancelled`;

      const { checkout_url } = await billingApi.createCheckoutSession(
        successUrl,
        cancelUrl
      );

      // Redirect to Stripe checkout
      window.location.href = checkout_url;
    } catch (error) {
      console.error('[UpgradeModalProvider] Failed to create checkout session:', error);
      setCheckoutError('Unable to start checkout. Please try again.');
    }
  }, []);

  const handleClose = useCallback(() => {
    setCheckoutError(null);
    hideModal();
  }, [hideModal]);

  // Gate: only show the upgrade modal for self-service users.
  // Default to NOT showing the modal while loading to prevent flicker.
  if (!isSelfService || isLoadingWorkspaces) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      {limitInfo && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={handleClose}
          resource={limitInfo.resource}
          used={limitInfo.used}
          limit={limitInfo.limit}
          onUpgrade={handleUpgrade}
          resetsAt={limitInfo.resetsAt ? new Date(limitInfo.resetsAt) : undefined}
          error={checkoutError}
        />
      )}
    </>
  );
}
